/**
 * Formats duration in seconds to MM:SS or HH:MM:SS format
 *
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (MM:SS or HH:MM:SS)
 *
 * @example
 * formatDuration(45) // "00:45"
 * formatDuration(154) // "02:34"
 * formatDuration(3754) // "1:02:34"
 */
export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const pluralize = (value: number, unit: string): string =>
  `${value} ${unit}${value === 1 ? '' : 's'}`;

/**
 * The same duration spelled for a screen reader — `02:34` is read as "zero two
 * colon three four", which is not a duration.
 *
 * @param seconds - Duration in seconds
 * @returns Spoken duration, e.g. "2 minutes 34 seconds"
 *
 * @example
 * formatDurationForSpeech(45) // "45 seconds"
 * formatDurationForSpeech(60) // "1 minute"
 * formatDurationForSpeech(154) // "2 minutes 34 seconds"
 * formatDurationForSpeech(3754) // "1 hour 2 minutes 34 seconds"
 */
export const formatDurationForSpeech = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds) || 0);

  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const secs = safeSeconds % 60;

  const parts: string[] = [];
  if (hours > 0) {
    parts.push(pluralize(hours, 'hour'));
  }
  if (minutes > 0) {
    parts.push(pluralize(minutes, 'minute'));
  }
  if (secs > 0) {
    parts.push(pluralize(secs, 'second'));
  }

  return parts.length > 0 ? parts.join(' ') : '0 seconds';
};
